import React, { useState, useMemo } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import GalleryLightbox from '../components/GalleryLightbox';
import { MAIN_GALLERIES, COMPANY_INFO as STATIC_COMPANY_INFO } from '../data/siteData';
import { useGalleryCategories, useCompanyInfo, usePageData, useBeforeAfter } from '../lib/cms';
import { sortImagesByUploadDate } from '../lib/imageSorting';

export default function GalleryPage() {
  const { categories: GALLERY_CATEGORIES } = useGalleryCategories();
  const { companyInfo: COMPANY_INFO } = useCompanyInfo();
  const { page } = usePageData('galleri');
  const { pairs: BEFORE_AFTER_PAIRS } = useBeforeAfter();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get('kategori') || 'all';

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeLightboxImages, setActiveLightboxImages] = useState([]);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const heroBg = page.heroImage || '';

  // Forbered de 4 hovedgallerier som matcher menuens dropdown 1:1, med opdaterede billeder fra CMS
  const enrichedMainGalleries = useMemo(() => {
    return MAIN_GALLERIES.map((main) => {
      const images = [];
      const seenUrls = new Set();

      main.subCategoryIds.forEach((subId) => {
        const subCats = GALLERY_CATEGORIES.filter((c) => {
          const cid = (c.id || '').toLowerCase();
          const cslug = (c.slug || '').toLowerCase();
          const csys = (c.sysSlug || '').toLowerCase();
          return cid === subId.toLowerCase() || cslug === subId.toLowerCase() || csys === subId.toLowerCase();
        });

        subCats.forEach((subCat) => {
          if (subCat && subCat.images && subCat.images.length > 0) {
            subCat.images.forEach((img) => {
              const rawFilename = (img.url || '').split('/').pop().toLowerCase();
              const normalizedKey = rawFilename.startsWith('image_') || rawFilename.startsWith('image.')
                ? rawFilename
                : rawFilename.replace(/_[a-f0-9]{8,}(?=\.[a-z0-9]+$)/i, '');
              if (!seenUrls.has(img.url) && !seenUrls.has(normalizedKey)) {
                seenUrls.add(img.url);
                if (normalizedKey) seenUrls.add(normalizedKey);
                images.push({
                  ...img,
                  categoryTitle: subCat.title,
                  categoryId: subCat.id,
                  mainTitle: main.title
                });
              }
            });
          }
        });
      });

      // Sortér billederne så nyeste projekter og uploads altid ligger øverst
      const sortedImages = sortImagesByUploadDate(images);

      // Coverbillede: brug senest tilføjede billede (første i listen) fra CMS
      const coverImage = (sortedImages[0] && sortedImages[0].url) || '';

      return {
        ...main,
        images: sortedImages,
        coverImage,
        imageCount: sortedImages.length
      };
    });
  }, [GALLERY_CATEGORIES]);

  // Find aktivt hovedgalleri
  const activeMainData = useMemo(() => {
    if (categoryParam === 'all') return null;
    return enrichedMainGalleries.find((g) => g.slug === categoryParam || g.id === categoryParam) || null;
  }, [categoryParam, enrichedMainGalleries]);

  // Håndter eventuel direkte subkategori slug (bagudkompatibilitet)
  const currentViewData = useMemo(() => {
    if (activeMainData) return activeMainData;

    if (categoryParam !== 'all') {
      const subCat = GALLERY_CATEGORIES.find((c) => c.slug === categoryParam || c.id === categoryParam);
      if (subCat) {
        const sortedSubImages = sortImagesByUploadDate(subCat.images || []);
        return {
          id: subCat.id,
          slug: subCat.slug,
          title: subCat.title,
          description: subCat.description,
          icon: "fa-solid fa-camera",
          images: sortedSubImages,
          imageCount: sortedSubImages.length
        };
      }
    }

    return null;
  }, [activeMainData, categoryParam, GALLERY_CATEGORIES]);

  // Saml alle billeder på tværs med nyeste tilføjede billeder først
  const allImages = useMemo(() => {
    const list = [];
    const seen = new Set();
    enrichedMainGalleries.forEach((main) => {
      main.images.forEach((img) => {
        const rawFilename = (img.url || '').split('/').pop().toLowerCase();
        const normalizedKey = rawFilename.startsWith('image_') || rawFilename.startsWith('image.')
          ? rawFilename
          : rawFilename.replace(/_[a-f0-9]{8,}(?=\.[a-z0-9]+$)/i, '');
        if (!seen.has(img.url) && !seen.has(normalizedKey)) {
          seen.add(img.url);
          if (normalizedKey) seen.add(normalizedKey);
          list.push({
            ...img,
            mainCategorySlug: main.slug,
            mainCategoryTitle: main.title
          });
        }
      });
    });
    return sortImagesByUploadDate(list);
  }, [enrichedMainGalleries]);

  const openLightboxForImages = (images, startIndex = 0) => {
    setActiveLightboxImages(images);
    setActiveImageIndex(startIndex);
    setLightboxOpen(true);
  };

  const handleSelectCategory = (slug) => {
    if (slug === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ kategori: slug });
    }
  };

  return (
    <>
      <SEO
        title={currentViewData ? `${currentViewData.title} Galleri - CMSNord.dk` : "Galleri - CMSNord.dk"}
        description="Se vores galleri over udført entreprenørarbejde, belægning, haveservice og vognmandskørsel i Brønderslev og hele Nordjylland."
        canonicalUrl={`${window.location.origin}/galleri`}
        ogImage="https://assets.auxodev.dk/assets/dde638d5-f83c-4036-8d58-4f922ae20635/1-20-281-29-0_1e0156d9.webp"
      />

      {/* 1. PANORAMA HERO STRIP */}
      <section className="relative bg-[#121212] text-white py-16 sm:py-20 border-b border-zinc-800 overflow-hidden">
        {heroBg && (
          <div className="absolute inset-0 z-0">
            <img
              src={heroBg}
              alt="CMSNord.dk Galleri"
              className="w-full h-full object-cover object-center opacity-55"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#121212]/95 via-[#121212]/80 to-[#121212]/60" />
          </div>
        )}

        {/* Large Background Watermark Text */}
        <div className="absolute right-4 sm:right-12 -bottom-6 z-0 select-none pointer-events-none opacity-5 font-black text-7xl sm:text-9xl uppercase tracking-tighter text-white">
          GALLERI
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#F4AF00]/20 text-[#F4AF00] rounded-sm text-xs font-bold uppercase tracking-widest mb-3">
            <i className="fa-solid fa-camera" aria-hidden="true" />
            <span>Dokumenteret håndværk</span>
          </div>

          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-white uppercase tracking-tight">
            {currentViewData ? `${currentViewData.title} Galleri` : 'Billedgalleri'}
          </h1>
          <p className="text-zinc-300 text-sm sm:text-base mt-2 max-w-2xl">
            {currentViewData
              ? currentViewData.description
              : 'Se et udvalg af vores udførte opgaver inden for entreprenørarbejde, belægning, haveservice og vognmandskørsel.'}
          </p>

          {/* KATEGORI FILTER TABS (matcher Header dropdown præcist) */}
          <div className="mt-8 flex items-center gap-2 overflow-x-auto no-scrollbar py-1">
            <button
              onClick={() => handleSelectCategory('all')}
              className={`px-4 py-2 text-xs font-black uppercase tracking-wider rounded transition-all cursor-pointer shrink-0 flex items-center gap-2 ${
                categoryParam === 'all'
                  ? 'bg-[#F4AF00] text-[#121212] shadow-md scale-102'
                  : 'bg-zinc-800/90 hover:bg-zinc-700 text-zinc-200'
              }`}
            >
              <i className="fa-solid fa-grid-2" aria-hidden="true" />
              <span>Alle Billeder</span>
            </button>

            {enrichedMainGalleries.map((g) => {
              const isActive = categoryParam === g.slug || categoryParam === g.id;
              return (
                <button
                  key={g.slug}
                  onClick={() => handleSelectCategory(g.slug)}
                  className={`px-4 py-2 text-xs font-black uppercase tracking-wider rounded transition-all cursor-pointer shrink-0 flex items-center gap-2 ${
                    isActive
                      ? 'bg-[#F4AF00] text-[#121212] shadow-md scale-102'
                      : 'bg-zinc-800/90 hover:bg-zinc-700 text-zinc-200'
                  }`}
                >
                  <i className={`${g.icon} ${isActive ? 'text-[#121212]' : 'text-[#F4AF00]'}`} aria-hidden="true" />
                  <span>{g.title}</span>
                </button>
              );
            })}

            <NavLink
              to="/galleri/foer-efter"
              className="px-4 py-2 text-xs font-black uppercase tracking-wider rounded transition-all shrink-0 flex items-center gap-2 bg-zinc-800/90 hover:bg-[#F4AF00] text-zinc-200 hover:text-[#121212]"
            >
              <i className="fa-solid fa-arrows-left-right text-[#F4AF00]" aria-hidden="true" />
              <span>Før &amp; Efter</span>
            </NavLink>
          </div>
        </div>
      </section>

      {/* 2. OVERSIGT MED DE HOVEDOMRÅDER DER MATCHER DROPDOWN'EN (VISES NÅR 'ALL' ER VALGT) */}
      {categoryParam === 'all' && (
        <section className="py-12 sm:py-16 bg-white text-[#121212] border-b border-zinc-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="mb-8">
              <span className="text-[#D99B00] text-xs font-extrabold uppercase tracking-wider block mb-1">
                Arbejdsområder
              </span>
              <h2 className="text-2xl sm:text-3xl font-black text-[#121212] uppercase tracking-tight">
                Vælg et galleri
              </h2>
              <p className="text-zinc-600 text-sm mt-1">
                Klik på et område for at se de tilhørende billeder, eller se alle billeder nedenfor.
              </p>
            </div>

            {/* 5 PRIMÆRE GALLERIKORT (Entreprenør, Belægning, Have, Vognmandskørsel, Før & Efter) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
              {enrichedMainGalleries.map((main) => (
                <div
                  key={main.slug}
                  onClick={() => handleSelectCategory(main.slug)}
                  className="group bg-white border border-zinc-300 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between cursor-pointer border-b-4 border-b-zinc-300 hover:border-b-[#F4AF00] rounded-sm hover:-translate-y-1"
                >
                  <div className="relative aspect-[4/3] w-full overflow-hidden bg-zinc-900">
                    {main.coverImage ? (
                      <img
                        src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/${main.coverImage.replace('https://assets.auxodev.dk/', '')}`}
                        alt={main.title}
                        className="w-full h-full object-cover group-hover:scale-108 transition-transform duration-500"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-zinc-800 text-zinc-500">
                        <i className={`${main.icon} text-4xl opacity-40`} aria-hidden="true" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                    <div className="absolute top-2.5 right-2.5 bg-[#121212]/90 backdrop-blur-sm text-white px-2.5 py-0.5 text-[11px] font-black uppercase tracking-wider border border-zinc-700 rounded-xs flex items-center gap-1.5 shadow">
                      <i className="fa-solid fa-camera text-[#F4AF00]" aria-hidden="true" />
                      <span>{main.imageCount}</span>
                    </div>

                    <div className="absolute bottom-2.5 left-2.5 right-2.5 text-white">
                      <div className="flex items-center gap-2 mb-0.5">
                        <i className={`${main.icon} text-sm text-[#F4AF00]`} aria-hidden="true" />
                        <h3 className="font-black text-sm uppercase tracking-wide text-white drop-shadow">
                          {main.title}
                        </h3>
                      </div>
                    </div>
                  </div>

                  <div className="p-3.5 flex flex-col justify-between flex-1 bg-zinc-50 border-t border-zinc-200">
                    <p className="text-[11px] text-zinc-600 line-clamp-2 leading-relaxed mb-3">
                      {main.description}
                    </p>
                    <div className="flex items-center justify-between pt-2 border-t border-zinc-200 text-xs font-black uppercase tracking-wider text-zinc-800 group-hover:text-[#D99B00] transition-colors">
                      <span>Åbn galleri</span>
                      <span className="w-6 h-6 rounded-full bg-zinc-200 group-hover:bg-[#F4AF00] group-hover:text-[#121212] flex items-center justify-center transition-colors">
                        <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              {/* FØR & EFTER KORT */}
              <NavLink
                to="/galleri/foer-efter"
                className="group bg-white border border-zinc-300 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col justify-between cursor-pointer border-b-4 border-b-zinc-300 hover:border-b-[#F4AF00] rounded-sm hover:-translate-y-1"
              >
                <div className="relative aspect-[4/3] w-full overflow-hidden bg-zinc-900">
                  {BEFORE_AFTER_PAIRS[0]?.after?.url || BEFORE_AFTER_PAIRS[0]?.before?.url ? (
                    <img
                      src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/${(BEFORE_AFTER_PAIRS[0].after.url || BEFORE_AFTER_PAIRS[0].before.url).replace('https://assets.auxodev.dk/', '')}`}
                      alt="Før & Efter"
                      className="w-full h-full object-cover group-hover:scale-108 transition-transform duration-500"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-zinc-800 text-zinc-500">
                      <i className="fa-solid fa-arrows-left-right text-4xl opacity-40" aria-hidden="true" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                  <div className="absolute top-2.5 right-2.5 bg-[#F4AF00] text-[#121212] px-2.5 py-0.5 text-[11px] font-black uppercase tracking-wider rounded-xs flex items-center gap-1 shadow">
                    <i className="fa-solid fa-arrows-left-right text-xs" aria-hidden="true" />
                    <span>Før / Efter</span>
                  </div>

                  <div className="absolute bottom-2.5 left-2.5 right-2.5 text-white">
                    <div className="flex items-center gap-2 mb-0.5">
                      <i className="fa-solid fa-arrows-left-right text-sm text-[#F4AF00]" aria-hidden="true" />
                      <h3 className="font-black text-sm uppercase tracking-wide text-white drop-shadow">
                        Før &amp; Efter
                      </h3>
                    </div>
                  </div>
                </div>

                <div className="p-3.5 flex flex-col justify-between flex-1 bg-zinc-50 border-t border-zinc-200">
                  <p className="text-[11px] text-zinc-600 line-clamp-2 leading-relaxed mb-3">
                    Se fantastiske forvandlinger og dokumenterede resultater fra vores have- og belægningsarbejde.
                  </p>
                  <div className="flex items-center justify-between pt-2 border-t border-zinc-200 text-xs font-black uppercase tracking-wider text-zinc-800 group-hover:text-[#D99B00] transition-colors">
                    <span>Se før/efter</span>
                    <span className="w-6 h-6 rounded-full bg-zinc-200 group-hover:bg-[#F4AF00] group-hover:text-[#121212] flex items-center justify-center transition-colors">
                      <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                    </span>
                  </div>
                </div>
              </NavLink>
            </div>
          </div>
        </section>
      )}

      {/* 3. ALLE BILLEDER (NYESTE FØRST/ØVERST) */}
      {categoryParam === 'all' && (
        <section className="py-14 sm:py-20 bg-[#F4F4F6] text-[#121212] border-b border-zinc-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 pb-4 border-b border-zinc-300">
              <div>
                <span className="text-[#D99B00] text-xs font-black uppercase tracking-wider block mb-1">
                  Alle tilføjede fotos
                </span>
                <h2 className="text-2xl sm:text-3xl font-black text-[#121212] uppercase tracking-tight">
                  Alle Billeder
                </h2>
                <p className="text-zinc-600 text-xs sm:text-sm mt-1">
                  Viser {allImages.length} billeder fra entreprenørarbejde, belægning, have og vognmandskørsel.
                </p>
              </div>
            </div>

            {/* Grid of all images */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
              {allImages.map((img, idx) => (
                <div
                  key={`${img.url}-${idx}`}
                  onClick={() => openLightboxForImages(allImages, idx)}
                  className="group relative bg-white border border-zinc-300 overflow-hidden shadow-xs hover:shadow-lg transition-all cursor-pointer aspect-square rounded-sm"
                >
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/${img.url.replace('https://assets.auxodev.dk/', '')}`}
                    alt={img.caption || `CMSNord billede ${idx + 1}`}
                    className="w-full h-full object-cover group-hover:scale-108 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity p-3 flex flex-col justify-between">
                    <span className="self-end p-1.5 rounded bg-black/60 text-[#F4AF00] text-xs">
                      <i className="fa-solid fa-expand" aria-hidden="true" />
                    </span>
                    <div>
                      {img.mainCategoryTitle && (
                        <span className="inline-block bg-[#F4AF00] text-[#121212] text-[9px] font-black uppercase px-1.5 py-0.5 mb-1 rounded-2xs">
                          {img.mainCategoryTitle}
                        </span>
                      )}
                      <p className="text-white text-xs font-bold leading-snug drop-shadow line-clamp-2">
                        {img.caption}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 4. FILTERED GALLERY VIEW (ENTREPRENØR, BELÆGNING, HAVE, VOGNMAND) */}
      {categoryParam !== 'all' && currentViewData && (
        <section className="py-14 sm:py-20 bg-[#F4F4F6] text-[#121212] border-b border-zinc-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8 pb-6 border-b border-zinc-300">
              <div>
                <button
                  onClick={() => handleSelectCategory('all')}
                  className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-wider text-zinc-600 hover:text-[#121212] mb-3 transition-colors cursor-pointer"
                >
                  <i className="fa-solid fa-arrow-left text-[#F4AF00]" aria-hidden="true" />
                  <span>Tilbage til alle gallerier</span>
                </button>
                <div className="flex items-center gap-3">
                  <i className={`${currentViewData.icon} text-2xl text-[#F4AF00]`} aria-hidden="true" />
                  <h2 className="text-2xl sm:text-4xl font-black text-[#121212] uppercase tracking-tight">
                    {currentViewData.title}
                  </h2>
                </div>
                <p className="text-zinc-600 text-sm mt-1 max-w-2xl">
                  {currentViewData.description}
                </p>
              </div>

              <div className="flex items-center gap-3 shrink-0">
                <span className="text-xs text-zinc-500 font-bold bg-white px-3 py-1.5 rounded border border-zinc-200">
                  <i className="fa-solid fa-camera text-[#F4AF00] mr-1.5" aria-hidden="true" />
                  {currentViewData.images.length} fotos
                </span>
                <button
                  onClick={() => handleSelectCategory('all')}
                  className="px-3.5 py-1.5 text-xs font-black uppercase tracking-wider bg-zinc-200 hover:bg-zinc-300 text-[#121212] rounded transition-colors"
                >
                  Vis alle
                </button>
              </div>
            </div>

            {/* Grid of photos in this category (nyeste først) */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
              {currentViewData.images.map((img, idx) => (
                <div
                  key={`${img.url}-${idx}`}
                  onClick={() => openLightboxForImages(currentViewData.images, idx)}
                  className="group relative bg-white border border-zinc-300 overflow-hidden shadow-xs hover:shadow-xl transition-all cursor-pointer aspect-square rounded-sm"
                >
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/${img.url.replace('https://assets.auxodev.dk/', '')}`}
                    alt={img.caption || `${currentViewData.title} billede ${idx + 1}`}
                    className="w-full h-full object-cover group-hover:scale-108 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity p-3 flex flex-col justify-between">
                    <span className="self-end p-1.5 rounded bg-black/60 text-[#F4AF00] text-xs">
                      <i className="fa-solid fa-expand" aria-hidden="true" />
                    </span>
                    <p className="text-white text-xs font-bold leading-snug drop-shadow line-clamp-2">
                      {img.caption}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 5. CALL TO ACTION / KONTAKT OS */}
      <section className="py-16 sm:py-20 bg-white text-[#121212] border-b border-zinc-300">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center space-y-6">
          <div className="flex items-center justify-center gap-2">
            <span className="w-3 h-3 rounded-full bg-[#F4AF00]" />
            <span className="text-xs font-black uppercase tracking-wider text-zinc-500">
              Har du et projekt i tankerne?
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl font-black text-[#121212] uppercase tracking-tight">
            Lad os hjælpe med dit næste projekt
          </h2>

          <p className="text-zinc-700 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
            Uanset om du skal have udgravet, etableret ny indkørsel, ryddet haven eller bestilt container til affald, står CMSNord.dk klar med professionelt udstyr og mange års erfaring.
          </p>

          <div className="pt-4 flex flex-wrap justify-center items-center gap-4 sm:gap-6">
            <a
              href={`tel:${COMPANY_INFO.phoneRaw}`}
              className="nowrap-contact px-8 py-3.5 bg-[#121212] hover:bg-zinc-800 text-white font-black text-sm uppercase tracking-wider transition-all shadow-lg hover:scale-105 flex items-center gap-3"
            >
              <i className="fa-solid fa-phone text-[#F4AF00] text-base" aria-hidden="true" />
              <span>Ring: {COMPANY_INFO.phone}</span>
            </a>

            <NavLink
              to="/kontakt"
              className="px-8 py-3.5 bg-[#F4AF00] hover:bg-[#D99B00] text-[#121212] font-black text-sm uppercase tracking-wider transition-all shadow-lg hover:scale-105 flex items-center gap-2"
            >
              <span>Få et uforpligtende tilbud</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </NavLink>
          </div>
        </div>
      </section>

      {/* Fullscreen Lightbox Modal */}
      <GalleryLightbox
        images={activeLightboxImages}
        isOpen={lightboxOpen}
        initialIndex={activeImageIndex}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
}
